import { NavContext } from '@ionic/react';
import { Button, Modal } from 'antd';
import { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { GenericCard } from '../../components/common/GenericCard/GenericCard';
import { ContextApp } from '../../contexts/ContextApp';
import './HomePage.less';
import { AvailableQuotaCard } from '../../components/common/AvailableQuotaCard/AvailableQuotaCard';
import useNotificationCards from '../../hooks/useNotificationCards';
import { FeatureFlagWrapper } from '../FeatureFlagWrapper/FeatureFlagWrapper';
import { EnumsValues } from '../../enums/EnumsValues';
import { useLocation } from 'react-router-dom';
import { tourContext } from '../../contexts/TourContext';
import useFeatureFlagConfig from '../FeatureFlagWrapper/hooks/useFeatureFlagConfig';
import { PageLoading } from '@ant-design/pro-layout';
import GraphqlService from '../../services/graphql/GraphqlService';
import { Mutation } from '../../services/graphql/mutation';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const HomePage = () => {
  const { t, setMainMenuCollapsed, user, roles } = useContext(ContextApp);
  const { customRequest, Query } = GraphqlService();

  const { navigate } = useContext(NavContext);
  const { errorCard } = useNotificationCards();
  const location = useLocation();
  const { createTour } = useContext(tourContext);
  const [textSection, setTextSection] =
    useState<{ id: number; title: string; content: string } | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newContent, setNewContent] = useState('');

  useEffect(() => {
    const fetchSection = async () => {
      setLoading(true);
      console.log(loading);
      const response = await customRequest({
        query: Query.getHomeTextSection,
      });

      if (response?.__typename === 'HomeTextSectionResponse') {
        let content = response.content;

        //si no tiene formato, se le aplica formato por defecto
        if (!hasHtmlFormatting(content)) {
          content = applyBulletFormat(content);
        }

        setTextSection({
          ...response,
          content,
        });
      }
      setLoading(false);
    };

    fetchSection();
  }, []);

  const isFirstNotification = async (): Promise<boolean> => {
    if (!user) return false;

    try {
      const result = await customRequest({
        query: Query.getSeenTourByIdAndUserId,
        variables: {
          tour_id: EnumsValues.TourSections.SmartDocsNotification,
          user_id: user.id,
        },
      });

      const seenNotification = result?.getSeenTourByIdAndUserId ?? result;

      return !seenNotification;
    } catch (error) {
      return true;
    }
  };

  useEffect(() => {
    const checkNotification = async () => {
      const isFirst = await isFirstNotification();
      if (isFirst) {
        Modal.confirm({
          content: (
            <>
              <div>
                <p>
                  <b>¡Ahora Comprobantes está dentro de SmartDocs™!</b> <br />{' '}
                  Ingresá allí para cargar, consultar y gestionar tus documentos
                  como siempre.
                </p>
              </div>
            </>
          ),
          okText: 'Entendido',
          maskClosable: true,
          keyboard: true,
          okCancel: false,
          onOk: async () => {
            try {
              await customRequest({
                mutation: Mutation.createSeenTour,
                variables: {
                  tour_id: EnumsValues.TourSections.SmartDocsNotification,
                  user_id: user?.id,
                },
              });
            } catch (error) {
              console.error(error);
            }
          },
        });
      }
    };

    checkNotification();
  }, [user]);

  const handleUpdate = async () => {
    const response = await customRequest({
      mutation: Mutation.updateHomeTextSection,
      variables: {
        input: {
          id: textSection?.id,
          content: newContent,
        },
      },
    });
    if (response?.__typename === 'HomeTextSectionResponse') {
      setTextSection(response);
      setIsEditing(false);
    }
  };

  useLayoutEffect(() => {
    const isFromHelpPage = sessionStorage.getItem('fromHelpPage') === 'true';
    if (
      location.state?.runTour &&
      location.pathname === '/app/home' &&
      isFromHelpPage
    ) {
      setMainMenuCollapsed(false);
      createTour(EnumsValues.TourSections.Menu);
      sessionStorage.setItem('fromHelpPage', 'false');
    }
  }, []);

  const handleEdit = () => {
    setNewContent(textSection?.content || '');
    setIsEditing(true);
  };

  const { loading: loadingMethodsFeatureFlag } = useFeatureFlagConfig({
    featureFlag: EnumsValues.FeatureFlags.methodsPage,
    publicAccess: false,
  });

  const { loading: loadingPacksFeatureFlag } = useFeatureFlagConfig({
    featureFlag: EnumsValues.FeatureFlags.packsPage,
    publicAccess: false,
  });

  const loadingFeatureFlags =
    loadingMethodsFeatureFlag || loadingPacksFeatureFlag;

  const isOrganizationUser = () => {
    const [role] = roles;
    if (role) {
      return (
        // Puede un usuario tener mas de un rol? si puede, quiero limitar solo al que tiene un unico rol TenantUser
        roles.length === 1 &&
        role.name === EnumsValues.SystemRolesNames.TenantUser
      );
    }
    return false;
  };

  const hasHtmlFormatting = (html: string): boolean => {
    if (!html) return false;
    const regex = /<(b|strong|i|em|u|span|a|ul|ol|li|h1|h2|h3|blockquote)/i;
    return regex.test(html);
  };

  const applyBulletFormat = (text: string): string => {
    if (!text) return '';

    // reemplaza guiones por saltos de línea
    const cleaned = text.replace(/-/g, '\n');

    // separar en oraciones / líneas
    const lines = cleaned
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0); // eliminar vacíos

    // convierte a <li>
    const liItems = lines.map((line) => `<li>${line}</li>`).join('');

    // envuelve en una lista UL
    return `<ul>${liItems}</ul>`;
  };

  const isSuperAdmin = () => {
    const [role] = roles;
    if (role) {
      return (
        roles.length === 1 && role.name === EnumsValues.SystemRolesNames.Admin
      );
    }
    return false;
  };

  return (
    <div className="homepage">
      {errorCard}
      <div className="homepage__header--container">
        <div className="homepage__titles--container">
          <p className="homepage__title">
            {t('homePage.title', {
              firstname: user?.firstname,
            })}
          </p>
          <p className="homepage__subtitle">{t('homePage.subtitle')}</p>
        </div>
        <AvailableQuotaCard version="2" />
      </div>
      <div className="homepage_card">
        <GenericCard
          icon={
            <span
              className="material-symbols-outlined homepage__generic-card--icon"
              translate="no"
            >
              flag_circle
            </span>
          }
          title={t('homePage.charge')}
          className="homepage__generic-card--charge"
          onCardClick={() => navigate('/app/smartDocs/documents/receipt')}
          button={
            <Button className="homePage-btn-start">
              {t('homePage.letsStart!')}
              <span
                className="material-symbols-outlined arrow-start"
                translate="no"
              >
                arrow_right_alt
              </span>
            </Button>
          }
          description={t('homePage.chargeDescription')}
          orientation="horizontal"
        />
        <GenericCard
          icon={
            <span
              className="material-symbols-outlined homepage__generic-card--icon"
              translate="no"
            >
              campaign
            </span>
          }
          title={t('homePage.newFeature')}
          showEditIcon={isSuperAdmin()} //solo visible para el super admin
          onEditClick={handleEdit}
          className="homepage__generic-card--charge"
          description={
            <div
              className="ql-homepage-content"
              dangerouslySetInnerHTML={{ __html: textSection?.content || '' }}
            />
          }
          orientation="horizontal"
        />
        <Modal
          title="Editar descripción"
          open={isEditing}
          onOk={handleUpdate}
          onCancel={() => setIsEditing(false)}
          okText="Guardar"
          cancelText="Cancelar"
        >
          <ReactQuill
            theme="snow"
            value={newContent}
            onChange={setNewContent}
            modules={{
              toolbar: [
                ['bold', 'italic', 'underline', 'strike'],
                ['link'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['clean'],
              ],
            }}
          />
        </Modal>
      </div>

      {!loadingFeatureFlags ? (
        <div>
          <p className="homepage__other-recomendations">
            {t('homePage.otherRecomendations')}
          </p>
          <div className="homepage__generic-card--container">
            {!isOrganizationUser() && (
              <div style={{ flex: 1 }}>
                <GenericCard
                  icon={
                    <span
                      className="material-symbols-outlined homepage__generic-card--icon"
                      translate="no"
                    >
                      settings
                    </span>
                  }
                  title={t('tenant_setting.generalSetting')}
                  description={t('tenant_setting.descriptionSettingCard')}
                  className="homepage__generic-card--credits homepage__generic-card--recomendation"
                  orientation="vertical"
                  onCardClick={() => navigate('/app/smartDocs/setting/general')}
                />
              </div>
            )}

            <div style={{ flex: 1 }}>
              <GenericCard
                icon={
                  <span
                    className="material-symbols-outlined homepage__generic-card--icon"
                    translate="no"
                  >
                    dvr
                  </span>
                }
                title={t('page.concepts')}
                description={t('Concept.conceptDescriptionCard')}
                className="homepage__generic-card--integration homepage__generic-card--recomendation"
                orientation="vertical"
                onCardClick={() =>
                  navigate('/app/smartDocs/administration/concepts')
                }
              />
            </div>
            <FeatureFlagWrapper
              featureFlag={EnumsValues.FeatureFlags.packsPage}
              publicAccess={false}
              loadingComponent={null}
            >
              <GenericCard
                icon={
                  <span
                    className="material-symbols-outlined homepage__generic-card--icon"
                    translate="no"
                  >
                    monetization_on
                  </span>
                }
                title={t('homePage.credits')}
                description={t('homePage.creditsDescription')}
                className="homepage__generic-card--credits homepage__generic-card--recomendation"
                orientation="vertical"
                onCardClick={() => navigate('/app/smartDocs/packs')}
              />
            </FeatureFlagWrapper>
          </div>
        </div>
      ) : (
        <PageLoading></PageLoading>
      )}
    </div>
  );
};
export default HomePage;
